import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Search, Building2, User, Target, FileText, ShoppingCart, CornerDownLeft, Clock, Sparkles } from 'lucide-react';
import { searchAll } from '../../api';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useShortcuts } from '../../keyboard/ShortcutContext';
import { useAssistant } from '../../assistant/AssistantContext';
import { buildCommands } from './commands';
import { scoreCommand } from './fuzzy';
import './CommandPalette.css';

// ⌘K. One box, three behaviours, decided by what is typed: a name jumps to
// the record, a verb runs a command, and (from Phase 4) anything else is a
// question for the assistant.

const TYPE_META = {
  account: { label: 'Account', icon: Building2 },
  contact: { label: 'Contact', icon: User },
  opportunity: { label: 'Opportunity', icon: Target },
  quote: { label: 'Quote', icon: FileText },
  order: { label: 'Order', icon: ShoppingCart },
};

const RECENT_KEY = 'crm_palette_recent_v1';
const RECENT_MAX = 8;
const DEBOUNCE_MS = 150;

const readRecent = () => {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list : [];
  } catch (e) {
    return [];
  }
};
const remember = (record) => {
  try {
    const list = readRecent().filter((r) => !(r.type === record.type && r.id === record.id));
    localStorage.setItem(RECENT_KEY, JSON.stringify([record, ...list].slice(0, RECENT_MAX)));
  } catch (e) { /* storage unavailable — nothing to remember */ }
};

export default function CommandPalette({ open, onClose }) {
  const navigate = useNavigate();
  const { user, isModuleVisible, canDo, hasPermission } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { setSheetOpen } = useShortcuts();
  const { openWith: askAssistant } = useAssistant();
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setDebounced('');
    setActive(0);
    const timer = setTimeout(() => inputRef.current && inputRef.current.focus(), 20);
    return () => clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query.trim()), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  const search = useQuery({
    queryKey: ['search', debounced],
    queryFn: () => searchAll(debounced, 5).then((r) => r.data),
    enabled: open && debounced.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
  });

  const commands = useMemo(() => buildCommands({
    navigate, isModuleVisible, canDo, hasPermission,
    isAdmin: user && user.role_name === 'Admin',
    theme, toggleTheme,
    openShortcuts: () => setSheetOpen(true),
    askAssistant,
  }), [navigate, isModuleVisible, canDo, hasPermission, user, theme, toggleTheme, setSheetOpen, askAssistant]);

  // The flat list the keyboard walks, with group headers for the eye.
  const items = useMemo(() => {
    const out = [];
    const q = query.trim();
    if (!q) {
      const recent = readRecent();
      if (recent.length) {
        out.push({ header: 'Recent' });
        recent.forEach((r) => out.push({ kind: 'record', record: r, recent: true }));
      }
      let group = null;
      commands.forEach((c) => {
        if (c.group !== group) { group = c.group; out.push({ header: group }); }
        out.push({ kind: 'command', command: c });
      });
      return out;
    }
    const matched = commands
      .map((c) => ({ c, s: scoreCommand(q, c) }))
      .filter((x) => x.s >= 60)
      .sort((a, b) => b.s - a.s)
      .slice(0, 4);
    if (matched.length) {
      out.push({ header: 'Commands' });
      matched.forEach(({ c }) => out.push({ kind: 'command', command: c }));
    }
    const results = (search.data && search.data.results) || [];
    let type = null;
    results.forEach((r) => {
      if (r.type !== type) { type = r.type; out.push({ header: `${TYPE_META[type] ? TYPE_META[type].label : type}s` }); }
      out.push({ kind: 'record', record: r });
    });
    // Anything else is a question for the assistant.
    if (q.length >= 2) { out.push({ header: 'Ask' }); out.push({ kind: 'ask', text: q }); }
    return out;
  }, [query, commands, search.data]);

  const selectable = useMemo(() => items.map((it, i) => (it.header ? null : i)).filter((i) => i !== null), [items]);

  useEffect(() => { setActive(0); }, [query, search.data]);

  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector('[aria-selected="true"]');
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [active]);

  if (!open) return null;

  const choose = (item) => {
    if (!item) return;
    onClose();
    if (item.kind === 'record') {
      remember({ type: item.record.type, id: item.record.id, title: item.record.title, subtitle: item.record.subtitle, path: item.record.path });
      navigate(item.record.path);
    } else if (item.kind === 'command') {
      item.command.run();
    } else if (item.kind === 'ask') {
      askAssistant(item.text);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, selectable.length - 1)); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); return; }
    if (e.key === 'Home') { e.preventDefault(); setActive(0); return; }
    if (e.key === 'End') { e.preventDefault(); setActive(Math.max(selectable.length - 1, 0)); return; }
    if (e.key === 'Enter') { e.preventDefault(); choose(items[selectable[active]]); }
    // Tab hands whatever is typed to the assistant, whatever is highlighted.
    if (e.key === 'Tab' && !e.shiftKey && query.trim().length >= 2) { e.preventDefault(); choose({ kind: 'ask', text: query.trim() }); }
  };

  const activeId = selectable.length ? `palette-item-${selectable[active]}` : undefined;
  const q = query.trim();
  const nothing = q && items.filter((it) => !it.header && it.kind !== 'ask').length === 0 && !search.isFetching;

  return (
    <div className="palette-overlay modal-overlay" onMouseDown={onClose} role="presentation">
      <div
        className="palette"
        role="dialog"
        aria-modal="true"
        aria-label="Search and commands"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="palette-input-row">
          <Search size={18} className="palette-input-icon" aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            className="palette-input"
            placeholder="Search accounts, contacts, deals… or type a command"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            role="combobox"
            aria-expanded="true"
            aria-controls="palette-list"
            aria-activedescendant={activeId}
            aria-autocomplete="list"
            autoComplete="off"
            spellCheck="false"
          />
          <kbd className="palette-esc">esc</kbd>
        </div>

        <ul id="palette-list" className="palette-list" role="listbox" ref={listRef} aria-label="Results">
          {items.map((item, i) => {
            if (item.header) return <li key={`h-${i}`} className="palette-header" role="presentation">{item.header}</li>;
            const selected = selectable[active] === i;
            if (item.kind === 'command') {
              const Icon = item.command.icon;
              return (
                <li
                  key={`c-${item.command.id}`}
                  id={`palette-item-${i}`}
                  role="option"
                  aria-selected={selected}
                  className={`palette-item ${selected ? 'is-active' : ''}`}
                  onMouseEnter={() => setActive(selectable.indexOf(i))}
                  onClick={() => choose(item)}
                >
                  {Icon && <Icon size={16} className="palette-item-icon" aria-hidden="true" />}
                  <span className="palette-item-title">{item.command.label}</span>
                  <span className="palette-item-type">{item.command.group}</span>
                </li>
              );
            }
            if (item.kind === 'ask') {
              return (
                <li
                  key="ask"
                  id={`palette-item-${i}`}
                  role="option"
                  aria-selected={selected}
                  className={`palette-item ${selected ? 'is-active' : ''}`}
                  onMouseEnter={() => setActive(selectable.indexOf(i))}
                  onClick={() => choose(item)}
                >
                  <Sparkles size={16} className="palette-item-icon" aria-hidden="true" />
                  <span className="palette-item-title">Ask the assistant: “{item.text}”</span>
                  <span className="palette-item-type"><kbd>Tab</kbd> Assistant</span>
                </li>
              );
            }
            const meta = TYPE_META[item.record.type] || { label: item.record.type, icon: Search };
            const Icon = item.recent ? Clock : meta.icon;
            return (
              <li
                key={`r-${item.record.type}-${item.record.id}-${item.recent ? 'recent' : ''}`}
                id={`palette-item-${i}`}
                role="option"
                aria-selected={selected}
                className={`palette-item ${selected ? 'is-active' : ''}`}
                onMouseEnter={() => setActive(selectable.indexOf(i))}
                onClick={() => choose(item)}
              >
                <Icon size={16} className="palette-item-icon" aria-hidden="true" />
                <span className="palette-item-text">
                  <span className="palette-item-title">{item.record.title}</span>
                  {item.record.subtitle && <span className="palette-item-subtitle">{item.record.subtitle}</span>}
                </span>
                <span className="palette-item-type">{meta.label}</span>
              </li>
            );
          })}
          {nothing && (
            <li className="palette-empty" role="presentation">No matches for “{q}”.</li>
          )}
        </ul>

        <div className="palette-footer">
          <span>{search.isFetching ? 'Searching…' : search.isError ? 'Search is unavailable right now.' : ''}</span>
          <span className="palette-hints">
            <kbd>↑</kbd><kbd>↓</kbd> move · <kbd><CornerDownLeft size={11} aria-hidden="true" /></kbd> open · <kbd>Tab</kbd> ask the AI · <kbd>esc</kbd> close
          </span>
        </div>
      </div>
    </div>
  );
}
